<template>
    <DCard
        padding
        :title="__('customers.manage_clusters.customer_clusters')"
    >
        <DTable
            :items="customerClusters"
            :fields="fields"
        >
            <template #actions="{ item }">
                <td class="text-end">
                    <DButtonDropdown
                        class="manage-clusters-dropdown"
                        icon="dots-horizontal"
                    >
                        <DDropdownItem
                            icon="shuffle-variant"
                            @click="onMoveClusterClick(item)"
                        >
                            {{ __('customers.manage_clusters.move_cluster_and_contracts') }}
                        </DDropdownItem>
                    </DButtonDropdown>
                </td>
            </template>
        </DTable>
        <template v-if="unstableContracts.length > 0">
            <hr class="my-4">
            <h5 class="mt-2">
                {{ __('customers.manage_clusters.unstable_contracts') }}
            </h5>
            <p class="text-warning fw-bold">
                {{ __('customers.manage_clusters.unstable_contracts_note') }}
            </p>
            <DTable
                :items="unstableContracts"
                :fields="unstableContractsFields"
            />
        </template>
    </DCard>
    <DCard
        padding
        :title="__('customers.manage_clusters.dedicated_clusters')"
    >
        <DButton
            class="mb-3"
            :label="__('customers.manage_clusters.move_all_dedicated_clusters')"
            :disabled="canMoveAllDedicatedClusters === false"
            @click="onMoveAllDedicatedClustersClick"
        />
        <DTable
            :items="dedicatedClusters"
            :fields="dedicatedClustersFields"
        >
            <template #contract_id="{ item }">
                <td>
                    {{ item.contracts[0]?.id }}
                </td>
            </template>
            <template #status="{ item }">
                <td>
                    {{ item.contracts[0]?.status }}
                    <span
                        v-if="isClusterStable(item) === false"
                        class="text-warning fw-bold"
                    >
                        <DIcon name="alert" />
                        {{ __('customers.manage_clusters.cannot_move_contract') }}
                    </span>
                </td>
            </template>
            <template #actions="{ item }">
                <td class="text-end">
                    <DButtonDropdown
                        class="manage-clusters-dropdown"
                        icon="dots-horizontal"
                    >
                        <DDropdownItem
                            icon="shuffle-variant"
                            :disabled="isClusterStable(item) === false"
                            @click="onMoveClusterClick(item)"
                        >
                            {{ __('customers.manage_clusters.move_cluster_and_contracts') }}
                        </DDropdownItem>
                    </DButtonDropdown>
                </td>
            </template>
        </DTable>
    </DCard>
    <MoveClusterModal
        :show-modal="showMoveClusterModal"
        :cluster="selectedCluster"
        @close-modal="showMoveClusterModal = false"
    />
</template>

<script>
import App from '~@/App.vue'
import CustomerBase from '~@/Pages/Customers/Base'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import MoveClusterModal from '~@/Components/Customers/ManageClusters/Modals/MoveClusterModal'

export default {
    layout: [App, CustomerBase]
}
</script>

<script setup>
const props = defineProps({
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    },
    clusters: {
        type: Array,
        default: () => {
            return []
        }
    },
    unstableContracts: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const fields = [
    { key: 'id', label: __('customers.manage_clusters.cluster_id') },
    { key: 'brand', label: __('customers.manage_clusters.vanguard_brand') },
    { key: 'actions', label: '' }
]

const dedicatedClustersFields = [
    { key: 'id', label: __('customers.manage_clusters.cluster_id') },
    { key: 'contract_id', label: __('customers.manage_clusters.contract_id') },
    { key: 'brand', label: __('customers.manage_clusters.vanguard_brand') },
    { key: 'status', label: __('customers.manage_clusters.contract_status') },
    { key: 'actions', label: '' }
]

const unstableContractsFields = [
    { key: 'id', label: __('customers.manage_clusters.contract_id') },
    { key: 'status', label: __('customers.manage_clusters.contract_status') }
]

const customerClusters = computed(() => {
    return props.clusters.filter((cluster) => cluster.is_dedicated === false)
})

const dedicatedClusters = computed(() => {
    return props.clusters.filter((cluster) => cluster.is_dedicated === true)
})

const isClusterStable = (cluster) => {
    return cluster.contracts.every((contract) => contract.is_stable === true)
}

const canMoveAllDedicatedClusters = computed(() => {
    return dedicatedClusters.value.length > 0 && dedicatedClusters.value.every((cluster) => isClusterStable(cluster))
})

const showMoveClusterModal = ref(false)
const selectedCluster = ref({ id: null, is_dedicated: false, contracts: [] })

const onMoveClusterClick = (cluster) => {
    selectedCluster.value = cluster
    showMoveClusterModal.value = true
}

const onMoveAllDedicatedClustersClick = () => {
    selectedCluster.value = {
        id: null,
        is_dedicated: true,
        contracts: dedicatedClusters.value.flatMap((cluster) => cluster.contracts)
    }
    showMoveClusterModal.value = true
}
</script>

<style lang="scss">
.manage-clusters-dropdown .d-dropdown {
    z-index: 1060 !important;
}
</style>
